<template>

  <div v-if="currentView === LoadingView" key="loading">
    <component :is="currentView" />
  </div>
  <div v-else class="d-flex flex-column min-vh-100" key="main-content">
    <div v-if="user && user.confirmed">
      <HeaderTemplate :current-view="currentView.name" @change-view="handleViewChange" @logout="handleLogout" />
    </div>

    <!-- Layout con Sidebar e Main Content -->
    <div class="d-flex flex-column flex-grow-1" :style="user && user.confirmed ? '' : ''">
      <div class="d-flex flex-grow-1">
        <!-- Sidebar -->
        <div v-if="user && user.confirmed">
          <SidebarTemplate :current-view="currentView.name" @change-view="handleViewChange" @logout="handleLogout" />
        </div>

        <!-- Main Content -->
        <div class="flex-grow-1"
          :style="{ marginLeft: user && user.confirmed ? '16rem' : '0', marginRight: user && user.confirmed ? '0' : '0', marginTop: user && user.confirmed ? '3rem' : '0' }">
          <div class="container-fluid h-100 main-content" :class="{ 'p-0 m-0': !user || !user.confirmed }">
            <div class="app-container" :class="{ 'p-0 m-0': !user || !user.confirmed }">
              <component :is="currentView" :elementId="elementId" @login-success="handleLoginSuccess"
                @change-view="(view, id) => changeView(views[view], id)" class="fade-transition"
                @reset-element="handleResetElement" @reset-original-element="handleResetOriginalElement"></component>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div v-if="user && user.confirmed" class="mt-auto">
        <FooterTemplate />
      </div>
    </div>
  </div>
  <ToastDialog ref="toastRef" :title="toastTitle" :message="toastMessage" :error="toastError" />
</template>

<script>
import { ref, getCurrentInstance, provide, onMounted, watch, reactive } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import HeaderTemplate from './components/template/Header.vue';
import FooterTemplate from './components/template/Footer.vue';
import LoginView from './components/views/LoginView.vue';
import SidebarTemplate from './components/template/Sidebar.vue'
import ResetPasswordView from './components/views/ResetPasswordView.vue'
import DashboardView from './components/views/DashboardView.vue'
import LoadingView from './components/views/LoadingView.vue'
import QuestionView from './components/views/questions/QuestionView.vue'
import UserDetailView from './components/views/user/UserDetailView.vue'
import QuestionsView from './components/views/questions/QuestionsView.vue'
import QuestionDetailView from './components/views/questions/QuestionDetailView.vue'
import QuestionDetailExpertView from './components/views/questions/QuestionDetailExpertView.vue';
import { ContentTree } from './classes/tree.js'
import lodash from 'lodash'
import TiptapEditor from './components/common/TiptapEditor.vue';
import ArticlesView from './components/views/ArticlesView.vue';
import ArticleView from './components/views/ArticleView.vue';
import { nextTick } from 'vue';
import ToastDialog from './components/common/ToastDialog.vue';
import { useToast } from './composable/useToast';
import eventBus from './services/eventBus';
import ElencoDownloadView from './components/views/ElencoDownloadView.vue';
import QuestionsCountDownView from './components/views/questions/QuestionsCountDownView.vue';
import QuestionsToAssignView from './components/views/questions/QuestionsToAssignView.vue';
import StatisticsView from './components/views/StatisticsView.vue';
import QuestionDetailUOCView from './components/views/uoc/QuestionDetailUOCView.vue';
import SalaAttesaSalaUOCView from './components/views/uoc/SalaAttesaSalaUOCView.vue';
import SessioneUOCView from './components/views/uoc/SessioneUOCView.vue';
import StoricoUOCView from './components/views/uoc/StoricoUOCView.vue';
import UOCArchiveView from './components/views/uoc/UOCArchiveView.vue';
import StatisticsUOCView from './components/views/StatisticsUOCView.vue';
import UserListView from './components/views/user/UserListView.vue';
import SettingsView from './components/views/SettingsView.vue';
import ProfileView from './components/views/ProfileView.vue';

export default {
  name: 'App',
  components: {
    HeaderTemplate,
    FooterTemplate,
    LoginView,
    SidebarTemplate,
    ResetPasswordView,
    DashboardView,
    LoadingView,
    QuestionView,
    UserDetailView,
    QuestionsView,
    QuestionDetailView,
    QuestionDetailExpertView,
    TiptapEditor,
    ArticlesView,
    ArticleView,
    ToastDialog,
    ElencoDownloadView,
    QuestionsCountDownView,
    QuestionsToAssignView,
    StatisticsView,
    QuestionDetailUOCView,
    SalaAttesaSalaUOCView,
    SessioneUOCView,
    StoricoUOCView,
    UOCArchiveView,
    StatisticsUOCView,
    UserListView,
    SettingsView,
    ProfileView
  },
  setup() {
    const { appContext } = getCurrentInstance()
    const api = appContext.config.globalProperties.$api;
    const authService = appContext.config.globalProperties.$authService;
    const config = appContext.config.globalProperties.$config;
    const router = useRouter();
    const route = useRoute();
    const user = ref(null);
    const elementId = ref(undefined);
    //const currentView = ref(LoginView);
    const currentView = ref(LoadingView);
    const articleLoading = ref(false);
    const currentArticleRequest = ref(null);
    const pdfPollingInterval = ref(null);
    const startPdfPollingFlag = ref(false);
    const toastRef = ref(null);
    const { toastTitle, toastMessage, toastError, showSuccessToast, showErrorToast } = useToast(toastRef);
    const stableQuestionId = ref(undefined);


    const storedTheme = localStorage.getItem('theme');
    const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    const darkmode = ref(storedTheme === 'dark' || (storedTheme === null && prefersDark));
    const dlgs = ref({});

    const contents = ref(undefined);
    const contentTree = ref(undefined);
    const contentsLoading = ref(false);
    const breadcrumbData = reactive({
      section: undefined,
      article: undefined,
    });

    provide('user', user);

    const views = {
      LoginView,
      ResetPasswordView,
      DashboardView,
      LoadingView,
      QuestionView,
      UserDetailView,
      QuestionsView,
      QuestionDetailView,
      QuestionDetailExpertView,
      ArticlesView,
      ArticleView,
      ElencoDownloadView,
      QuestionsCountDownView,
      QuestionsToAssignView,
      StatisticsView,
      QuestionDetailUOCView,
      SalaAttesaSalaUOCView,
      SessioneUOCView,
      StoricoUOCView,
      UOCArchiveView,
      StatisticsUOCView,
      UserListView,
      SettingsView,
      ProfileView
    };


    async function startPdfPolling() {
      if (pdfPollingInterval.value) return;

      pdfPollingInterval.value = setInterval(async () => {
        try {
          const res = await api.get('/user/pdf-status');
          const files = res.data.files;
          const hasPending = res.data.hasPending;

          if (files.length > 0) {
            const ids = [];

            files.forEach(file => {
              if (file.status === 'done') {
                
                ids.push(file.id);
                showSuccessToast(`Il PDF "${file.file_name}" è pronto per il download!`);
                eventBus.emit('pdf-ready');
              }
            });
            if (ids.length > 0) {
            await api.post('/user/pdf-mark-notified', { ids });
            }
          }
          if (
            res.data &&
            res.data.files.length === 0 &&
            !res.data.hasPendingJobs
          ) {
            stopPdfPolling();
          }

        } catch (err) {
          console.error('Errore polling PDF:', err);
          stopPdfPolling();
        }
      }, 5000);
    }

    function stopPdfPolling() {
      if (pdfPollingInterval.value) {
        clearInterval(pdfPollingInterval.value);
        pdfPollingInterval.value = null;
        startPdfPollingFlag.value = false;
      }
    }

    async function checkAuth(api, authService) {
      const token = localStorage.getItem('token');
      if (!token) {
        if (window.location.pathname.includes("/reset-password")) {
          changeView(ResetPasswordView);
          return;
        }
        localStorage.removeItem('currentView');
        changeView(LoginView);
        return;
      }

      // Recupera la vista e l'elementId salvati dal localStorage
      const savedView = localStorage.getItem('currentView');
      const savedElementId = localStorage.getItem('currentElementId');

      try {
        const userData = await authService.getUser();
        /**if (userData["user"]["roles"][0]["name"] == "user") {
          changeView(LoginView);
          return;
        }*/
        user.value = userData.user;
        if (!user.value?.confirmed) {
          changeView(LoginView);
          return;
        }

        // Ripristina la vista salvata e l'elementId o usa DashboardView come default
        if (savedView && views[savedView]) {
          let parsedElementId = undefined;

          if (savedElementId) {
            try {
              // Tenta di analizzare l'elementId come JSON
              parsedElementId = JSON.parse(savedElementId);
            } catch (e) {
              // Se non è un JSON valido, prova a convertirlo in numero
              parsedElementId = isNaN(parseInt(savedElementId)) ? savedElementId : parseInt(savedElementId);
            }
          }
          startPdfPolling();
          changeView(views[savedView], parsedElementId);
        } else {
          const uocPermissions = [
            'manage-uoc',
            'view-uoc',
            'view-report-uoc'
          ];

          const adminPermissions = [
            'manage-users'
          ];

          const hasAdminPermission = user.value.permissions?.some(p =>
            adminPermissions.includes(p)
          );

          const hasUocPermission = user.value.permissions?.some(p =>
            uocPermissions.includes(p)
          );
          if (hasAdminPermission) {
            changeView(UserListView);
          }
          else if (hasUocPermission) {
            changeView(SalaAttesaSalaUOCView);
          } else {
            changeView(QuestionsView);
          }
        }
      } catch (error) {
        // Se il token non è valido, rimuoviamo tutto
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        user.value = null;
        changeView(LoginView);
      }
    }

    async function handleLoginSuccess(userData) {
      user.value = userData.user;
      await checkAuth(api, authService);
    }

    function triggerPdfPolling() {
      startPdfPollingFlag.value = true;
    }


    function changeView(view, id) {
      elementId.value = id || undefined;
      currentView.value = view;

      if (view === views.QuestionsView) {
        router.push('/questions');
      } else if (view === views.QuestionView) {
        router.push('/questions/new');
      } else if (view === views.DashboardView) {
        router.push('/dashboard');
      }
      else if (view === views.QuestionDetailView) {
        router.push(`/questions/${id}`);
      }
      /**else if (view === views.QuestionDetailExpertView) {
        //router.push(`/questions/question/${id}`);
      }*/
      // Salva la vista corrente e l'elementId nel localStorage solo se non è LoginView
      if (view !== LoginView && view !== ResetPasswordView) {
        localStorage.setItem('currentView', view.name);
        if (id) {
          // Supporto per elementId come oggetto JSON
          if (typeof id === 'object') {
            localStorage.setItem('currentElementId', JSON.stringify(id));
          } else {
            localStorage.setItem('currentElementId', id.toString());
          }
        } else {
          localStorage.removeItem('currentElementId');
        }
      }
      window.history.pushState({}, '', '/cms/');
    }

    function handleViewChange(viewName, id) {
      if (views[viewName]) {
        changeView(views[viewName], id);
        if (viewName === 'ElencoDownloadView') {
          eventBus.emit('downloads-view-opened');
        }
      } else {
        console.error(`View ${viewName} not found`)
      }
    }

    function handleResetElement() {
      elementId.value = "0";
    }

    function handleResetOriginalElement(id) {
      elementId.value = id;
    }

    // Controlla l'autenticazione all'avvio dell'app
    onMounted(() => {
      document.cookie.split(';').forEach(c => {
        const name = c.split('=')[0].trim();
        if (['laravel_session', 'XSRF-TOKEN'].includes(name)) {
          localStorage.removeItem('token');
          document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
        }
      });
      //localStorage.removeItem('token');
      console.log('toastRef:', toastRef.value);
      const { appContext } = getCurrentInstance()
      const api = appContext.config.globalProperties.$api;
      const authService = appContext.config.globalProperties.$authService;
      //startPdfPolling();
      checkAuth(api, authService);

      // Apply theme based on darkmode value
      const theme = darkmode.value ? 'dark' : 'light';
      document.querySelector('html').setAttribute('data-bs-theme', theme);
      localStorage.setItem('theme', theme);

      // Listen for system preference changes
      window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', event => {
        if (localStorage.getItem('theme') === null) {
          const newTheme = event.matches ? 'dark' : 'light';
          darkmode.value = event.matches;
          document.querySelector('html').setAttribute('data-bs-theme', newTheme);
        }
      });
    });

    onMounted(async () => {
      await fetchDlgs();
      await fetchContents();
      //await fetchAccessMessages();
    });

    function loadBreadcrumb(node) {
      if (node === null) {
        breadcrumbData.section = null
        breadcrumbData.article = null
        return
      }
      if (node.type == "section") {
        breadcrumbData.section = node
        breadcrumbData.article = null
      } else {
        breadcrumbData.article = node
        breadcrumbData.section = null
      }
    }

    async function fetchArticle(articleId, versionId = '', validityDate = null) {

      // Se esiste già una richiesta in corso per lo stesso articolo, restituiscila
      if (articleLoading.value && currentArticleRequest.value) {
        return currentArticleRequest.value;
      }

      articleLoading.value = true;

      // Costruisci l'URL
      let url = `${config.VUE_APP_API_URL}/article/${articleId}/${versionId}`;
      if (validityDate) {
        url += `?validity_date=${validityDate}`;
      }

      // Crea e memorizza la promise
      currentArticleRequest.value = fetch(url)
        .then(response => {
          if (response.ok) {
            return response.json();
          }
          throw new Error('Failed to fetch article');
        })
        .catch(error => {
          console.error('Error fetching article:', error);
          throw error;
        })
        .finally(() => {
          articleLoading.value = false;
        });

      return currentArticleRequest.value;
    }


    async function fetchDlgs() {
      const response = await fetch(config.VUE_APP_API_URL + "/versions")
      if (response.ok) {
        const data = await response.json()
        if (data.status) {
          data.data.versions.forEach(dlg => {
            dlgs.value[dlg.id] = dlg;
          });
        }
      }
    }

    async function fetchContents() {

      // Evita caricamenti doppi
      if (contents.value || contentsLoading.value) {
        return;
      }

      contentsLoading.value = true;

      try {

        const response = await fetch(config.VUE_APP_API_URL + "/contents");
        if (response.ok) {
          const data = await response.json();
          contents.value = data.data;
          if (data.status) {
            contentTree.value = new ContentTree(lodash.cloneDeep(contents.value));
            console.log("Contents loaded successfully");
          }
        }
      } catch (error) {
        console.error("Error loading contents:", error);

      } finally {
        contentsLoading.value = false;
      }
    }

    function updateContentTree() {
      this.contentTree.nodes = [...this.contentTree.nodes];
    }


    function handleLogout() {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('currentView');
      localStorage.removeItem('currentElementId');
      stopPdfPolling();
      user.value = null;
      changeView(LoginView);
    }


    watch(startPdfPollingFlag, (newVal) => {
      if (newVal) startPdfPolling();
    });


    watch(
      () => route.fullPath,
      (newPath) => {
        if (newPath.includes('/questions/question')) {
          changeView(views.QuestionDetailExpertView);
        } else if (newPath.includes('/questions/new')) {
          changeView(views.QuestionView);
        } else if (/^\/questions\/\d+/.test(newPath)) {
          const id = newPath.split('/').pop();
          changeView(views.QuestionDetailView, id);
        } else if (newPath.includes('/questions')) {
          changeView(views.QuestionsView);
        } else if (newPath.includes('/dashboard')) {
          changeView(views.DashboardView);
        } else if (/^\/article\/\d+/.test(newPath)) {
          const segments = newPath.split('/');
          const articleId = segments[2];
          const version = segments[3] || undefined;
          changeView(views.QuestionDetailExpertView, articleId, version);
        } else if (newPath.includes('/articles')) {
          changeView(views.ArticlesView);
        } else if (newPath.includes('index.html')) {
          const searchParams = new URLSearchParams(window.location.search);
          const article = searchParams.get('article');
          const version = searchParams.get('version');
          /**if (article) {
            router.push({ name: 'article', params: { article: article, version: version || undefined } });
          }*/
          if (article) {
            const articleId = String(Number(article) || article);
            const safeVersion = version ?? null;

            nextTick(() => {
              router.push({ name: 'article', params: { article: articleId, version: safeVersion } }).catch(() => { });
            });
          }
        }
      }
    );

    return {
      currentView, changeView, user, views, darkmode, handleLoginSuccess, handleViewChange, handleLogout, elementId, api, authService, dlgs, contentTree, contents, contentsLoading, updateContentTree, fetchContents, fetchArticle, loadBreadcrumb, breadcrumbData, config, handleResetElement, triggerPdfPolling, toastMessage, handleResetOriginalElement,
      toastTitle,
      toastError,
      showSuccessToast,
      showErrorToast,
    };
  },
};
</script>

<style>
@import "./assets/css/bootstrap-icons/bootstrap-icons.css";

html,
body,
#app {
  /**font-family: 'Rockwell', sans-serif !important;*/
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  color: var(--bs-body-color);
  background-color: var(--bs-body-bg);
}

/* Import Bootstrap Icons e Inter font locali */
/**@import "./assets/css/bootstrap-icons/bootstrap-icons.css";


body {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  color: var(--bs-body-color);
  background-color: var(--bs-body-bg);
}*/

.min-vh-100 {
  min-height: 100vh;
}

.flex-grow-1 {
  flex-grow: 1;
}

/* Centra il LoginView */
.d-flex.justify-content-center.align-items-center {
  display: flex;
  justify-content: center;
  align-items: center;
}

.secondary-navbar {
  position: sticky;
  top: 56px;
  /* altezza della navbar principale */
  z-index: 1049;
  background-color: var(--bs-body-bg);
}

html,
body {
  height: 100%;
  margin: 0;
}

.container-fluid.main-content {
  height: 100%;
}
</style>